import {
  Alert,
  Box,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CompanyService from "../../Services/Company.service";
import { IOnboardingWizardState } from "../../Interfaces/IOnboardingWizardState";
import { isValidEmail } from "./OnboardingCapture.utils";
import { useWriteThrough, WriteThroughResult } from "./useWriteThrough";

type Props = {
  state: IOnboardingWizardState;
  companyRowID: string;
  frozen: boolean;
  registerFlush: (flush: () => Promise<WriteThroughResult>) => void;
  /** Set once an advance has been refused, so a blank required address is marked too (bug 6009). */
  showFieldErrors?: boolean;
};

/**
 * Step 4's client-level distribution settings (S4.4, story 5606): the shared mailbox and the
 * distribution note belong to the onboarding rather than to any one contact.
 *
 * With the mailbox on, its address must be valid before the step advances — and the shared
 * completion rule applies the same condition, so the meter cannot read complete while the step
 * refuses to advance. Switching the mailbox off keeps the stored address but the summary and the SOP
 * stop showing it, and the mailbox never counts toward the "someone must receive results" rule.
 */
const ContactDistributionSettings: React.FC<Props> = ({
  state,
  companyRowID,
  frozen,
  registerFlush,
  showFieldErrors,
}) => {
  const [enabled, setEnabled] = useState<boolean>(
    Boolean(state.contactSettings?.genericMailboxEnabled),
  );
  const [address, setAddress] = useState<string>(
    state.contactSettings?.genericMailboxAddress || "",
  );
  const [notes, setNotes] = useState<string>(
    state.contactSettings?.distributionNotes || "",
  );

  const writer = useWriteThrough<{
    genericMailboxEnabled: boolean;
    genericMailboxAddress: string;
    distributionNotes: string;
  }>((patch) =>
    CompanyService.saveOnboardingDraft({
      companyRowID,
      contacts: {
        genericMailboxEnabled: patch.genericMailboxEnabled,
        genericMailboxAddress: patch.genericMailboxAddress || null,
        distributionNotes: patch.distributionNotes || null,
      },
    }),
  );

  useEffect(() => {
    registerFlush(() => writer.flush());
  }, [registerFlush, writer]);

  const save = (changes: Partial<{ enabled: boolean; address: string; notes: string }>) => {
    if (frozen) return;
    writer.schedule({
      genericMailboxEnabled: changes.enabled ?? enabled,
      genericMailboxAddress: changes.address ?? address,
      distributionNotes: changes.notes ?? notes,
    });
  };

  // A malformed address is marked as it is typed; an empty one — also invalid, since the mailbox
  // being on makes the address required — is only marked once an advance has been refused, so a
  // freshly switched-on mailbox is not shown as already broken (bug 5668). Both fail the server's
  // one isValidEmail check, but this field's own error state only looked at the malformed half, so
  // an empty address blocked step 4 with the banner and nothing highlighted at all (bug 6009). The
  // same split step 5's Invoice Emailing Address settled on (bug 5832).
  const addressMalformed = enabled && Boolean(address.trim()) && !isValidEmail(address);
  const addressMissing = enabled && Boolean(showFieldErrors) && !address.trim();
  const addressInvalid = addressMalformed || addressMissing;

  return (
    <Paper elevation={0} sx={{ p: 2, mt: 2, border: "1px solid #e0e0e0" }}>
      <Typography variant="subtitle2" fontWeight={600}>
        Result distribution
      </Typography>

      <FormControlLabel
        control={
          <Switch
            size="small"
            disabled={frozen}
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              save({ enabled: e.target.checked });
            }}
          />
        }
        label="Send results to a shared mailbox as well"
      />

      {enabled && (
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            size="small"
            required
            label="Shared mailbox address"
            disabled={frozen}
            value={address}
            error={addressInvalid}
            helperText={
              addressMissing
                ? "Enter the shared mailbox address."
                : addressMalformed
                  ? "Enter a valid email address."
                  : " "
            }
            onChange={(e) => {
              setAddress(e.target.value);
              save({ address: e.target.value });
            }}
          />
        </Box>
      )}

      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        sx={{ mt: 1 }}
        label="Distribution notes"
        disabled={frozen}
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
          save({ notes: e.target.value });
        }}
      />

      <Alert severity="info" sx={{ mt: 2 }}>
        The shared mailbox is additional to the contacts above — it never satisfies the rule that at
        least one contact must receive results.
      </Alert>
    </Paper>
  );
};

export default ContactDistributionSettings;
